import { useState } from 'react'
import { Fuel, ChevronDown } from 'lucide-react'
import Typeahead from '../common/Typeahead'
import SaleReceipt from './SaleReceipt'
import { formatNaira } from '../../utils/formatters'

const PRODUCTS = ['AGO', 'PMS', 'DPK']
const PRODUCT_COLORS = {
  AGO: 'bg-amber-500',
  PMS: 'bg-blue-500',
  DPK: 'bg-green-500',
}

export default function CreditSaleForm({ currentUser, branches, companies, drivers, prices, onRecord, getPastDestinations, onAddCompany, onAddDriver }) {
  const branchId = currentUser.branchId
  const branch = branches.find((b) => b.id === branchId)

  const [companyId, setCompanyId] = useState('')
  const [driverId, setDriverId] = useState('')
  const [product, setProduct] = useState('AGO')
  const [litres, setLitres] = useState('')
  const [destination, setDestination] = useState('')
  const [destQuery, setDestQuery] = useState('')
  const [showDestSuggestions, setShowDestSuggestions] = useState(false)
  const [notes, setNotes] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [receipt, setReceipt] = useState(null)

  const branchCompanies = companies.filter((c) => c.branchId === branchId && c.active)
  const branchDrivers = drivers.filter((d) => d.branchId === branchId && d.active)
  const currentPrice = prices[branchId]?.[product] ?? null
  const litresNum = parseFloat(litres) || 0
  const totalValue = currentPrice ? litresNum * currentPrice.unitPrice : 0

  const pastDestinations = getPastDestinations(branchId).filter((d) =>
    destQuery ? d.toLowerCase().includes(destQuery.toLowerCase()) : true
  )

  function handleDestInput(val) {
    setDestination(val)
    setDestQuery(val)
    setShowDestSuggestions(true)
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    if (!companyId) { setError('Select a transport company.'); return }
    if (!driverId) { setError('Select a driver.'); return }
    if (!litres || litresNum <= 0) { setError('Enter a valid litres amount.'); return }
    if (!destination.trim()) { setError('Destination is required.'); return }
    if (!currentPrice) { setError(`No price set for ${product}. Ask your manager to set a price first.`); return }

    setSaving(true)
    const result = await onRecord({ branchId, transportCompanyId: companyId, driverId, productType: product, litres: litresNum, destination: destination.trim(), notes })
    setSaving(false)

    if (!result.success) { setError(result.message); return }
    setReceipt(result.sale)
  }

  function resetForm() {
    setCompanyId(''); setDriverId(''); setProduct('AGO')
    setLitres(''); setDestination(''); setDestQuery(''); setNotes(''); setError(''); setReceipt(null)
  }

  if (receipt) {
    return (
      <SaleReceipt
        sale={receipt}
        branch={branch}
        onNewSale={resetForm}
      />
    )
  }

  return (
    <div className="space-y-5">
      <div className="bg-blue-600 rounded-2xl p-4 text-white">
        <div className="flex items-center space-x-2 mb-1">
          <Fuel className="w-5 h-5 text-blue-200" />
          <p className="text-blue-100 text-sm font-medium">Credit Sale</p>
        </div>
        <p className="font-bold text-lg">{branch?.name || 'Branch'}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">

        {/* Transport Company — a hauler not on file yet can be added from here,
            so an unknown truck at the pump doesn't block the sale. */}
        <Typeahead
          label="Transport Company"
          placeholder="Search or type a new company…"
          items={branchCompanies.map((c) => ({ id: c.id, label: c.companyName, sub: c.contactPhone }))}
          value={companyId}
          onChange={(id) => setCompanyId(id)}
          onCreate={onAddCompany ? (name) => onAddCompany({ branchId, companyName: name }) : undefined}
          createLabel="Add company"
          required
        />

        {/* Driver */}
        <Typeahead
          label="Driver"
          placeholder="Search or type a new driver…"
          items={branchDrivers.map((d) => ({ id: d.id, label: d.fullName, sub: d.licencePlate }))}
          value={driverId}
          onChange={(id) => setDriverId(id)}
          onCreate={onAddDriver ? (name) => onAddDriver({ branchId, fullName: name }) : undefined}
          createLabel="Add driver"
          required
        />

        {/* Product type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Product</label>
          <div className="grid grid-cols-3 gap-2">
            {PRODUCTS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setProduct(p)}
                className={`py-3 rounded-xl text-sm font-bold transition border-2 ${
                  product === p
                    ? `${PRODUCT_COLORS[p]} text-white border-transparent`
                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        {/* Price display */}
        {currentPrice ? (
          <div className="bg-gray-50 rounded-xl px-4 py-3 flex items-center justify-between">
            <p className="text-sm text-gray-500">Current {product} price</p>
            <p className="text-lg font-bold text-gray-800">{formatNaira(currentPrice.unitPrice)}<span className="text-xs font-normal text-gray-400">/L</span></p>
          </div>
        ) : (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
            <p className="text-sm text-red-600 font-medium">No price set for {product}. Ask your manager to set a price.</p>
          </div>
        )}

        {/* Litres */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Litres Dispensed</label>
          <input
            type="number"
            value={litres}
            onChange={(e) => setLitres(e.target.value)}
            placeholder="0.00"
            min="0.01"
            step="0.01"
            inputMode="decimal"
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-xl font-bold text-center"
            required
          />
        </div>

        {/* Live total */}
        {litresNum > 0 && currentPrice && (
          <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-center justify-between">
            <p className="text-sm text-green-700">Total Value</p>
            <p className="text-2xl font-bold text-green-700">{formatNaira(totalValue)}</p>
          </div>
        )}

        {/* Destination */}
        <div className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Destination <span className="text-red-500">*</span></label>
          <input
            type="text"
            value={destination}
            onChange={(e) => handleDestInput(e.target.value)}
            onFocus={() => setShowDestSuggestions(true)}
            onBlur={() => setTimeout(() => setShowDestSuggestions(false), 150)}
            placeholder="e.g. Kano, Lagos, Ibadan…"
            className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
            required
          />
          {showDestSuggestions && pastDestinations.length > 0 && (
            <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-40 overflow-y-auto">
              {pastDestinations.slice(0, 8).map((d) => (
                <button
                  key={d}
                  type="button"
                  onMouseDown={() => { setDestination(d); setShowDestSuggestions(false) }}
                  className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm text-gray-700 border-b border-gray-50 last:border-0"
                >
                  {d}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Notes <span className="text-gray-400">(optional)</span></label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any additional notes…"
            rows={2}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm resize-none"
          />
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5">{error}</p>
        )}

        {/* Sticky submit — sits above the BottomNav */}
        {/* Right gutter on small screens keeps this clear of the floating
            support launcher, which sits in that corner. */}
        <div className="sticky bottom-[72px] bg-white pt-3 pb-2 -mx-4 px-4 pr-20 desk:pr-4 border-t border-gray-100">
          <button
            type="submit"
            disabled={saving || !currentPrice}
            className="w-full py-4 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold text-base transition"
          >
            {saving ? 'Recording…' : 'Record Credit Sale'}
          </button>
        </div>
      </form>
    </div>
  )
}
